/**
 * WorkspaceSidebar Component
 * New sidebar with hierarchical workspace-centric navigation
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { makeStyles, tokens, shorthands, Button, Spinner, mergeClasses } from '@fluentui/react-components';
import { SIDEBAR_MIN_WIDTH, SIDEBAR_MAX_WIDTH } from '../../utils/sidebarStateUtils';
import {
  ChevronLeft24Regular,
  ChevronRight24Regular,
  Building24Regular,
  Copy24Regular,
  Shield24Regular,
  People24Regular,
  BrainCircuit24Regular,
  Grid24Regular,
} from '@fluentui/react-icons';
import { WorkspacesNav } from './WorkspacesNav';
import { AdminNav } from './AdminNav';
import { workspaceApi } from '../../services/workspaceApi';
import { getWorkspaceColor } from '../../utils/workspaceColors';
import type { WorkspaceNavItem, AdminNavItem } from './types';

export interface WorkspaceSidebarProps {
  isCollapsed?: boolean;
  onToggle?: () => void;
  width?: number;
  onWidthChange?: (width: number) => void;
}

const useStyles = makeStyles({
  sidebar: {
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground4,
    ...shorthands.borderRight('1px', 'solid', tokens.colorNeutralStroke2),
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.padding(0, 0, tokens.spacingVerticalM, 0),
    transitionProperty: 'width',
    transitionDuration: '0.25s',
    transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
    overflowX: 'hidden',
    overflowY: 'hidden',
    boxSizing: 'border-box',
    position: 'relative' as const,
  },
  sidebarExpanded: {
    width: '260px', // Slightly wider for breathing room
  },
  sidebarCollapsed: {
    width: '64px',
  },
  navContent: {
    flex: '1',
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px', // Section spacing
    ...shorthands.padding('24px', 0),
    marginTop: '8px',
  },
  toggleButton: {
    marginTop: 'auto',
    marginLeft: tokens.spacingHorizontalM,
    marginRight: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalXS,
    color: tokens.colorNeutralForeground3,
    minWidth: '36px',
    height: '36px',
    ...shorthands.borderRadius('8px'),
    transitionProperty: 'all',
    transitionDuration: '0.15s',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '32px',
  },
  divider: {
    height: '1px',
    marginLeft: '20px',
    marginRight: '20px',
    // Subtle divider
    backgroundColor: tokens.colorNeutralStroke2,
  },
  dividerCollapsed: {
    marginLeft: '16px',
    marginRight: '16px',
  },
  resizeHandle: {
    position: 'absolute' as const,
    right: 0,
    top: 0,
    bottom: 0,
    width: '4px',
    cursor: 'col-resize',
    backgroundColor: 'transparent',
    transitionProperty: 'background-color',
    transitionDuration: '0.15s',
    ':hover': {
      backgroundColor: tokens.colorBrandBackground,
    },
  },
});

// Admin section children for global admins
const adminItems: AdminNavItem[] = [
  { id: 'organizations', label: 'Organizations', route: 'admin/organizations', icon: <Building24Regular /> },
  { id: 'tenant-mgmt', label: 'Tenant Mgmt', route: 'admin/tenant-management', icon: <Copy24Regular /> },
  { id: 'security', label: 'Security', route: 'admin/security', icon: <Shield24Regular /> },
  { id: 'users', label: 'Users', route: 'users', icon: <People24Regular /> },
  { id: 'system-settings', label: 'System Settings', route: 'admin/system-settings', icon: <BrainCircuit24Regular /> },
  // Dev-only items (tree-shaken in production builds)
  ...(import.meta.env.DEV ? [
    { id: 'templates', label: 'Templates', route: 'dev/templates', icon: <Grid24Regular /> },
  ] : []),
];

export function WorkspaceSidebar({ isCollapsed = false, onToggle, width, onWidthChange }: WorkspaceSidebarProps) {
  const styles = useStyles();
  const [workspaces, setWorkspaces] = useState<WorkspaceNavItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  useEffect(() => {
    async function loadWorkspaces() {
      try {
        setIsLoading(true);
        const data = await workspaceApi.listWorkspaces();
        // Map to nav items
        const navItems: WorkspaceNavItem[] = data.map((ws) => ({
          id: ws.id,
          label: ws.name,
          route: `workspaces/${ws.id}`,
          color: getWorkspaceColor(ws.id), // Use ID (tenant_id) for stable colors - name changes should not change color
        }));
        setWorkspaces(navItems);
      } catch (error) {
        // Silently fail - empty workspace list is fine
        setWorkspaces([]);
      } finally {
        setIsLoading(false);
      }
    }

    loadWorkspaces();
  }, []);

  const handleMouseDown = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    setIsResizing(true);
    startXRef.current = event.clientX;
    startWidthRef.current = width ?? 240;
    document.body.style.cursor = 'col-resize';
  }, [width]);

  const handleResizeKeyDown = useCallback((event: React.KeyboardEvent) => {
    const STEP = 10;
    const currentWidth = width ?? 240;

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      const newWidth = Math.min(SIDEBAR_MAX_WIDTH, currentWidth + STEP);
      onWidthChange?.(newWidth);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      const newWidth = Math.max(SIDEBAR_MIN_WIDTH, currentWidth - STEP);
      onWidthChange?.(newWidth);
    }
  }, [width, onWidthChange]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (event: MouseEvent) => {
      const deltaX = event.clientX - startXRef.current;
      const newWidth = Math.max(
        SIDEBAR_MIN_WIDTH,
        Math.min(SIDEBAR_MAX_WIDTH, startWidthRef.current + deltaX)
      );
      onWidthChange?.(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
    };
  }, [isResizing, onWidthChange]);

  // Determine sidebar width - disable transition during resize for instant feedback
  const sidebarStyle: React.CSSProperties = {};
  if (isResizing) {
    sidebarStyle.transition = 'none';
  }
  if (isCollapsed) {
    sidebarStyle.width = '60px';
  } else if (width !== undefined) {
    sidebarStyle.width = `${width}px`;
  }

  const sidebarClassName = `${styles.sidebar} ${isCollapsed ? styles.sidebarCollapsed : styles.sidebarExpanded}`;

  return (
    <nav role="navigation" className={sidebarClassName} style={sidebarStyle}>
      <div className={styles.navContent}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <Spinner size="small" />
          </div>
        ) : (
          <>
            <WorkspacesNav workspaces={workspaces} collapsed={isCollapsed} />
            <div className={mergeClasses(
              styles.divider,
              isCollapsed && styles.dividerCollapsed
            )} />
            <AdminNav items={adminItems} collapsed={isCollapsed} />
          </>
        )}
      </div>

      {onToggle && (
        <Button
          appearance="subtle"
          className={styles.toggleButton}
          icon={isCollapsed ? <ChevronRight24Regular /> : <ChevronLeft24Regular />}
          onClick={onToggle}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        />
      )}

      {/* Focusable separator for resizing - valid ARIA splitter pattern per WAI-ARIA 1.2 */}
      {!isCollapsed && (
        /* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */
        <div
          className={styles.resizeHandle}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize sidebar"
          aria-valuenow={width}
          aria-valuemin={SIDEBAR_MIN_WIDTH}
          aria-valuemax={SIDEBAR_MAX_WIDTH}
          tabIndex={0}
          onMouseDown={handleMouseDown}
          onKeyDown={handleResizeKeyDown}
        />
        /* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */
      )}
    </nav>
  );
}
